// src/components/layout/Layout.jsx
//
// App shell — 232px black sidebar + main column.
// The main column has: TopBar (search + bell + optional CTA), an optional
// PageHeader (eyebrow + serif H1 + optional CTA), then the page body.
//
// Pages that want the new page header pass `eyebrow`, `title`, and/or
// `rightSlot`. Pages that don't will fall back to the breadcrumbs banner.
//
// Props:
//   - eyebrow:        string above the page title
//   - title:          string | node — page H1
//   - rightSlot:      node — page-level CTA, aligned right of the H1
//   - topBarRight:    node — optional CTA in the top bar (mirrors the page CTA)
//   - hidePageHeader: bool — force-hide the page header even if title is set
//   - hideBreadcrumbs: bool
import React from "react";
import { Box } from "@mui/material";
import Navbar from "./Navbar";
import Footer from "./Footer";
import Breadcrumbs from "./Breadcrumbs";
import TopBar from "./TopBar";
import PageHeader from "./PageHeader";
import { APP_ENVIRONMENT } from "../../config";
import { layout } from "../../theme/tokens";

const SIDEBAR_WIDTH = layout.sidebarWidth;
const watermarkLabel = APP_ENVIRONMENT.toUpperCase();
const showEnvironmentWatermark =
  watermarkLabel === "UAT" || watermarkLabel === "DEV";

export default function Layout({
  children,
  eyebrow,
  title,
  rightSlot,
  topBarRight,
  hidePageHeader = false,
  hideBreadcrumbs = false,
}) {
  const showPageHeader = !hidePageHeader && (eyebrow || title);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        position: "relative",
        backgroundColor: "background.default",
      }}
    >
      {showEnvironmentWatermark && (
        <Box
          aria-hidden="true"
          sx={{
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
            userSelect: "none",
            zIndex: 1,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              fontSize: { xs: "13.5rem", md: "24rem", lg: "33rem" },
              fontWeight: 900,
              letterSpacing: "0.18em",
              lineHeight: 1,
              color: "rgba(201,161,75,0.10)",
              transform: "rotate(-32deg)",
              whiteSpace: "nowrap",
              textTransform: "uppercase",
            }}
          >
            {watermarkLabel}
          </Box>
        </Box>
      )}

      {/* SIDEBAR */}
      <Box
        sx={{
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
          position: "fixed",
          top: 0,
          left: 0,
          height: "100vh",
          zIndex: 3,
        }}
      >
        <Navbar />
      </Box>

      {/* MAIN */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          ml: `${SIDEBAR_WIDTH}px`,
          minWidth: 0,
          minHeight: "100vh",
          position: "relative",
          zIndex: 2,
        }}
      >
        <TopBar rightSlot={topBarRight} />

        {showPageHeader && (
          <PageHeader eyebrow={eyebrow} title={title} rightSlot={rightSlot} />
        )}

        <Box
          sx={{
            flex: 1,
            px: { xs: 2.5, md: 3.5 },
            py: 3.5,
            backgroundColor: "background.default",
          }}
        >
          {!hideBreadcrumbs && !showPageHeader && <Breadcrumbs />}
          {children}
        </Box>

        <Footer />
      </Box>
    </Box>
  );
}
